/**
 * Phase UI-12 — DocumentPreviewPane
 *
 * Shared preview pane used in SubmissionDetailPage (and later CompilerPage).
 * Shows a PDF inline when the document is a PDF, or a "no preview" fallback
 * for other file types. Has Approve/Reject buttons and shows classification
 * confidence.
 *
 * phase-fix-document-inline-viewer — the iframe no longer points at the file
 * endpoint directly: that endpoint serves Content-Disposition: attachment
 * (and, on Azure, 302s to a SAS URL), so browsers DOWNLOAD instead of
 * rendering — the regression Sharon hit the moment seeded documents gained
 * real files (f299f1c). The pane now fetches the bytes through the authed
 * client and renders a typed blob URL (the TemplateDetailPage preview
 * pattern), which renders inline regardless of the endpoint's disposition or
 * storage-driver redirects. The explicit Download affordances keep the
 * attachment behavior.
 */
import { useEffect, useState, type ReactElement } from 'react';
import { useInlineDocumentView } from '../../hooks/useInlineDocumentView';
import { useDocxHtmlPreview } from '../../hooks/useDocxHtmlPreview';
import { bandForPercent, confidencePercent } from '@/lib/confidence';
import { PreviewNavCluster } from './PreviewNavCluster';

/** Name the (unpreviewable) file type in the fallback, e.g. ".doc" or ".xlsx". */
function docxLabel(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : 'this';
}

export interface DocumentPreviewPaneProps {
  documentId: string;
  documentName: string;
  /** Explicit file URL. If omitted, constructed from documentId. */
  fileUrl?: string | null;
  /** Whether this is a PDF (for iframe). Defaults to checking filename. */
  isPdf?: boolean;
  /**
   * phase-fix-seed-docs-missing-files — whether the document actually has an
   * uploaded file behind it. When explicitly `false`, the pane shows a clear
   * explanatory state instead of pointing an iframe at the file endpoint, which
   * would render the raw `{"error":"NotFound","message":"Document has no
   * uploaded file"}` JSON (exactly Sharon's screenshot). Defaults to `true` so
   * existing callers are unchanged.
   */
  hasFile?: boolean;
  classificationStatus?: string | null;
  classificationConfidence?: number | null;
  onApprove?: () => void;
  onReject?: (reason: string) => void;
  onClose?: () => void;
  /** Navigation to previous document */
  onPrev?: () => void;
  /** Navigation to next document */
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
  /**
   * phase-fix-ui-screen-review-batch (Fix 2) — when the parent sequence is
   * locked, the review footer (Approve/Reject + confidence) is ABSENT, not
   * merely disabled: cd2259b silenced the rows and toolbar; this closes the
   * preview pane.
   */
  locked?: boolean;
}

export function DocumentPreviewPane({
  documentId,
  documentName,
  fileUrl,
  isPdf,
  hasFile = true,
  classificationStatus,
  classificationConfidence,
  onApprove,
  onReject,
  onClose,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
  locked = false,
}: DocumentPreviewPaneProps): ReactElement {
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const downloadUrl = fileUrl ?? `/api/v1/documents/${documentId}/file`;
  const isPdfFile = isPdf ?? documentName.toLowerCase().endsWith('.pdf');
  // phase-docx-preview — .docx renders inline via mammoth HTML (in a sandboxed
  // iframe). Legacy .doc is not mammoth-previewable and keeps the named
  // fallback below.
  const isDocxFile = !isPdfFile && documentName.toLowerCase().endsWith('.docx');

  // Inline view via THE shared path (phase-fix-compiler-preview-tab-download
  // extracted de22d2d's logic into useInlineDocumentView so every preview
  // surface renders blobs and none can drift back to the attachment endpoint).
  const { url: inlineUrl, error: inlineError, pageCount } = useInlineDocumentView(documentId, {
    enabled: hasFile && isPdfFile,
  });
  const {
    html: docxHtml,
    loading: docxLoading,
    error: docxError,
  } = useDocxHtmlPreview(documentId, { enabled: hasFile && isDocxFile });

  // phase-documents-tab-layout-batch (Item 4) — in-document paging. The pane
  // shows the browser's NATIVE PDF viewer (an <iframe> over a blob: URL), so
  // rather than ship a second, competing renderer we DRIVE the native view
  // via the URL fragment (#page=N) — one coherent paging model. The count is
  // parsed from the PDF bytes (hook).
  //
  // phase-fix-page-toggle-navigation — the control used to update `#page=N` on
  // the SAME iframe and advance its label immediately. But Chromium's PDF
  // viewer honours `#page` ONLY at document load — a fragment change on an
  // already-loaded PDF is IGNORED (confirmed: the src carried `#page=2` while
  // the native toolbar still read "1 / 5"). So the label led and the document
  // never moved. Two changes make one honest paging model:
  //  1. `requestedPage` drives the iframe `src` AND its `key`, so a page change
  //     REMOUNTS the iframe — a fresh load, which is the only point Chromium
  //     honours `#page=N`. (Approach (a): keep the native viewer, accept the
  //     brief reload flash. Rendering pages ourselves would mean a heavyweight
  //     PDF-canvas dependency for a preview pane — not worth it.)
  //  2. `shownPage` (the LABEL) follows the iframe's `onLoad`, never the click —
  //     so the label reflects what actually loaded and can never run ahead of
  //     the document. In unknowable-count mode this is what stops ▶ from
  //     stranding the label past the real end: with no successful (re)load the
  //     label does not advance.
  // The blob: URL is owned by the hook and stays stable across page changes
  // (same documentId), so remounting the iframe re-uses it — it is never
  // revoked mid-use.
  const [requestedPage, setRequestedPage] = useState(1);
  const [shownPage, setShownPage] = useState(1);
  useEffect(() => {
    setRequestedPage(1);
    setShownPage(1);
  }, [documentId]);

  // phase-forms-and-documents-review-batch (item 2) — tri-state paging:
  //  - known count > 1  → full "◀ Page x of y ▶" control.
  //  - unknown (null)   → plain "◀ ▶" arrows, no total.
  //  - known count == 1 → hide (confirmed single-page).
  const countKnown = pageCount !== null;
  const isSinglePage = pageCount === 1;
  const showPageControl = hasFile && isPdfFile && inlineUrl != null && !isSinglePage;
  // Bounds follow the DISPLAYED page (shownPage), not the requested one, so a
  // button never enables/disables ahead of what the viewer actually shows.
  const atFirstPage = shownPage <= 1;
  const atLastKnownPage = countKnown && shownPage >= (pageCount ?? 1);

  const goPrev = (): void => setRequestedPage(Math.max(1, shownPage - 1));
  const goNext = (): void =>
    setRequestedPage(countKnown ? Math.min(pageCount ?? shownPage, shownPage + 1) : shownPage + 1);

  // #page fragment on a freshly-mounted iframe (keyed below) navigates the
  // native viewer to the requested page.
  // phase-package-viewer-live-structure addendum 2 — fit-width (view=FitH),
  // matching the compiler viewers: one default across every preview surface.
  const pagedUrl = inlineUrl ? `${inlineUrl}#page=${requestedPage}&view=FitH` : null;

  // phase-compiler-screen-batch item 2 (Sharon, 2026-07-23) — this was the
  // FOURTH set of confidence boundaries in the product (85/60, alongside the
  // Inspector's 70/40, the tree's 40 and the classification list's 85/65).
  // Bands now come from the one shared helper. The caller already hands this
  // prop a 0–100 value (DocumentsTabPanel multiplies by 100 before passing it),
  // so it goes in under `ConfidenceScore`, which `confidencePercent` treats as
  // an already-percentage scale.
  const confidenceBand =
    classificationConfidence == null
      ? null
      : bandForPercent(confidencePercent({ ConfidenceScore: classificationConfidence }) ?? 0);
  const confidenceColor =
    confidenceBand === null
      ? 'var(--text-muted)'
      : confidenceBand === 'high'
        ? '#16a34a'
        : confidenceBand === 'medium'
          ? '#ca8a04'
          : '#dc2626';

  return (
    <div
      data-testid="document-preview-pane"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 8,
        overflow: 'hidden',
      }}
    >
      {/* Header — phase-seed-state-audit-and-preview-nav (Part B, Sharon's
          layout): the document ◀▶ and the page ◀▶ form ONE stacked navigation
          cluster on the left. The page arrows sit DIRECTLY UNDER the document
          arrows (same button geometry, so they align), with the page label
          BESIDE the arrows — flipping documents and flipping pages are now
          adjacent and the cursor barely moves. The page control used to be a
          separate full-width centred row below this header, horizontally
          distant from the document arrows.

          Everything else is unchanged: the filename and Download keep their
          places; the label still FOLLOWS displayed reality (the 8982e7f /
          phase-fix-page-toggle-navigation rule — shownPage advances only on the
          iframe's onLoad, never on the click); the unknowable-count mode still
          renders arrows without a total. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: 'var(--surface2)',
          borderBottom: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {/* phase-compiler-chrome-batch item 2 — the cluster is now THE shared
            PreviewNavCluster component (extracted verbatim), so the Compiler's
            preview header renders the identical geometry. The behaviour
            contracts stay here: shownPage still follows the iframe's onLoad
            (8982e7f) and unknowable-count mode still passes a total-less label
            with ▶ enabled. */}
        <PreviewNavCluster
          onPrevDocument={onPrev}
          onNextDocument={onNext}
          hasPrevDocument={hasPrev}
          hasNextDocument={hasNext}
          showPageControl={showPageControl}
          countKnown={countKnown}
          atFirstPage={atFirstPage}
          atLastPage={atLastKnownPage}
          onPrevPage={goPrev}
          onNextPage={goNext}
          pageLabel={countKnown ? `Page ${shownPage} of ${pageCount}` : `Page ${shownPage}`}
        />
        <span
          data-testid="preview-filename"
          style={{
            flex: 1,
            fontSize: 12,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {documentName}
        </span>
        {hasFile && (
          <a
            href={downloadUrl}
            download={documentName}
            data-testid="preview-download-btn"
            style={{ fontSize: 11, color: 'var(--primary)', textDecoration: 'none' }}
          >
            ↓ Download
          </a>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="close preview"
          data-testid="preview-close-btn"
          style={{
            padding: '2px 8px',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            fontSize: 16,
            color: 'var(--text-muted)',
          }}
        >
          ×
        </button>
      </div>

      {/* PDF / Fallback body */}
      <div style={{ flex: 1, overflow: 'hidden', background: '#f3f4f6' }}>
        {!hasFile ? (
          // phase-fix-seed-docs-missing-files — the document row exists but no
          // file has been uploaded behind it (a real pre-upload state). Explain
          // it instead of pointing an iframe at the file endpoint, which would
          // render the raw NotFound JSON.
          <div
            data-testid="preview-no-file"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 12,
              color: 'var(--text-muted)',
              padding: 24,
              textAlign: 'center',
            }}
          >
            <span style={{ fontSize: 40 }}>📭</span>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>No file uploaded yet</p>
            <p style={{ margin: 0, fontSize: 13, maxWidth: 360 }}>
              This document record has no file behind it yet. Upload a file to preview and
              download it here.
            </p>
          </div>
        ) : isPdfFile ? (
          inlineError ? (
            <div
              data-testid="preview-inline-error"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 8,
                color: 'var(--text-muted)',
              }}
            >
              <span style={{ fontSize: 32 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 13 }}>{inlineError}</p>
            </div>
          ) : inlineUrl ? (
            <iframe
              // Keying on the requested page REMOUNTS the iframe on a page
              // change — the fresh load is the only point Chromium's PDF viewer
              // honours #page=N. The blob: URL is stable across page changes
              // (same document), so this re-uses it rather than revoking it.
              key={pagedUrl ?? inlineUrl}
              src={pagedUrl ?? inlineUrl}
              title={documentName}
              data-testid="preview-iframe"
              // The label FOLLOWS the actual load: only once the (re)load fires
              // does shownPage catch up to the requested page. If a navigation
              // never loads, the label never advances (no stranding).
              onLoad={() => setShownPage(requestedPage)}
              style={{ width: '100%', height: '100%', border: 'none' }}
            />
          ) : (
            <div
              data-testid="preview-loading"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              Loading preview…
            </div>
          )
        ) : isDocxFile ? (
          docxError ? (
            <div
              data-testid="preview-docx-error"
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: 10,
                color: 'var(--text-muted)',
                padding: 24,
                textAlign: 'center',
              }}
            >
              <span style={{ fontSize: 32 }}>⚠️</span>
              <p style={{ margin: 0, fontSize: 13 }}>{docxError}</p>
              <a
                href={downloadUrl}
                download={documentName}
                style={{ fontSize: 13, color: 'var(--primary)' }}
              >
                ↓ Download {documentName}
              </a>
            </div>
          ) : docxHtml != null ? (
            <iframe
              // Sandboxed (no scripts) — mammoth HTML from an untrusted docx
              // renders inert. srcDoc keeps it self-contained, no blob URL.
              sandbox=""
              srcDoc={docxHtml}
              title={documentName}
              data-testid="preview-docx-html"
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            />
          ) : (
            <div
              data-testid="preview-loading"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'var(--text-muted)',
                fontSize: 13,
              }}
            >
              {docxLoading ? 'Rendering Word document…' : 'Loading preview…'}
            </div>
          )
        ) : (
          <div
            data-testid="preview-no-preview"
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              gap: 12,
              color: 'var(--text-muted)',
            }}
          >
            <span style={{ fontSize: 40 }}>📄</span>
            <p style={{ margin: 0, fontSize: 13 }}>
              No inline preview for {docxLabel(documentName)} files
            </p>
            <a
              href={downloadUrl}
              download={documentName}
              style={{ fontSize: 13, color: 'var(--primary)' }}
            >
              ↓ Download {documentName}
            </a>
          </div>
        )}
      </div>

      {/* Footer: confidence + Approve/Reject — ABSENT on a locked sequence
          (phase-fix-ui-screen-review-batch Fix 2). */}
      {locked ? null : (
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface2)',
          borderTop: '1px solid var(--border)',
          flexShrink: 0,
        }}
      >
        {classificationConfidence != null && (
          <div style={{ fontSize: 11, color: confidenceColor, marginBottom: 8 }}>
            Classification confidence: <strong>{classificationConfidence}%</strong>
            {classificationConfidence >= 85 && ' ✅'}
          </div>
        )}

        {rejectMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <input
              type="text"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Rejection reason (required)"
              data-testid="reject-reason-input"
              style={{
                padding: '6px 8px',
                border: '1px solid var(--border)',
                borderRadius: 4,
                fontSize: 12,
              }}
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => {
                  onReject?.(rejectReason);
                  setRejectMode(false);
                  setRejectReason('');
                }}
                disabled={!rejectReason.trim()}
                data-testid="reject-submit-btn"
                style={{
                  flex: 1,
                  padding: '5px 10px',
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: rejectReason.trim() ? 'pointer' : 'not-allowed',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                Confirm Reject
              </button>
              <button
                type="button"
                onClick={() => {
                  setRejectMode(false);
                  setRejectReason('');
                }}
                style={{
                  padding: '5px 10px',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: 'var(--surface)',
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {onApprove != null && classificationStatus !== 'Confirmed' && (
              <button
                type="button"
                onClick={onApprove}
                data-testid="preview-approve-btn"
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                ✓ Approve
              </button>
            )}
            {onReject != null && (
              <button
                type="button"
                onClick={() => setRejectMode(true)}
                data-testid="preview-reject-btn"
                style={{
                  flex: 1,
                  padding: '6px 12px',
                  background: '#dc2626',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                ✗ Reject
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
